<script>
import _ from 'lodash'

const TRACK_INTERCOM_EVENT = true
const ONE_HOUR_IN_MILLISECONDS = 36e5
const ONE_DAY_IN_MILLISECONDS = ONE_HOUR_IN_MILLISECONDS * 24

export default {
  name: 'TrackingMixin',

  data() {
    return {
      initialPhase: '',
      updateChanges: {
        what: 'what',
        why: 'why',
        where: 'where',
        when: 'when',
        who: 'who',
        how: 'how',
        howMuch: 'howMuch',
        expectation: 'expectation',
        reality: 'reality',
        conclusion: 'conclusion',
        measureAttachments: 'measureAttachments',
        measureDescription: 'measureDescription',
        businessRequirements: 'businessRequirements',
        name: 'name',
        collaborators: 'teamMember',
        coach: 'coach',
        searchCoach: 'searchCoach',
        teamMember: 'teamMember',
        searchTeamMember: 'searchTeamMember',
        challenge: 'challenge',
        changeLocation: 'changeLocation',
        changeTags: 'changeTags',
        visibility: 'visibility',
        problem: 'relateProblem',
        idea: 'relateIdea',
        expectedResult: 'relateResult',
        relatePhoto: 'relatePhoto',
        changeCategories: 'changeCategories',
        changeReviewCategories: 'changeReviewCategories',
        whatDidYouDo: 'experimentDone',
        experimentPhoto: 'experimentPhoto',
        verify: 'verify',
        experimentFailed: 'experimentFailed',
        solution: 'expandSustain',
        expandPhoto: 'expandPhoto',
        isImplemented: 'reviewImplemented',
        costReduction: 'reviewCostReductionPerMonth',
        revenue: 'reviewNewRevenuePerMonth',
        investment: 'reviewTotalInvestmentMade',
        estimationComment: 'reviewComments',
        implementation: 'implementationValidation',
        financial: 'financialValidation',
        highlight: 'highlight',
        addProblem: 'addProblem',
        problemDefinitionPhoto: 'problemDefinitionPhoto',
        defineAproach: 'defineAproach',
        containmentPlan: 'containmentPlan',
        containtmentPhoto: 'containtmentPhoto',
        rootCausePhoto: 'rootCausePhoto',
        rootCauseType: 'rootCauseType',
        rootcauseName: 'rootcauseName',
        rootcauseWhy: 'rootcauseWhy',
        correctiveAction: 'correctiveAction',
        correctiveActionWorked: 'correctiveActionWorked',
        preventiveAction: 'preventiveAction',
        evidencePhoto: 'evidencePhoto',
        addAttachment: 'addAttachment',
        completeRev: 'completeRev',
      },
    }
  },

  methods: {
    getRevCycle(type, rev) {
      switch (type) {
        case 'problemSolving':
          return _.get(rev, 'currentPhase', '')
        case 'improvementIdea':
          return _.get(rev, 'phase') || _.get(rev, 'revState')
        case 'quickAction':
          return _.get(rev, 'currentPhase')
        default:
          return null
      }
    },
    getRevType(rev) {
      if (rev?.type === 'rev') return 'improvementIdea'
      return rev?.type
    },

    getDurationInDays(rev) {
      if (!rev.createdAt) return null
      const revTimeCreated = new Date(rev?.createdAt)?.getTime?.()

      return Math.floor((Date.now() - revTimeCreated) / ONE_DAY_IN_MILLISECONDS)
    },

    getDurationInHours(rev) {
      if (!rev.createdAt) return null
      const revTimeCreated = new Date(rev?.createdAt)?.getTime?.()

      return Math.floor(
        (Date.now() - revTimeCreated) / ONE_HOUR_IN_MILLISECONDS
      )
    },

    trackView(rev) {
      const revType = this.getRevType(rev)

      this.$track(
        'rev.View',
        this.$store,
        {
          status: rev?.status ?? rev?.revCondition,
          revId: rev?._id ?? rev?.id,
          revType: revType,
        },
        TRACK_INTERCOM_EVENT
      )
    },

    trackRevNavigate(rev, phase, element) {
      const revType = this.getRevType(rev)

      this.$track(
        'rev.Navigate',
        this.$store,
        {
          tab: phase,
          revId: rev?._id ?? rev?.id,
          revType: revType,
          element: element ?? null,
          status: rev?.status ?? null,
        },
        TRACK_INTERCOM_EVENT
      )
    },

    trackImplementRev(data) {
      this.$track('validation.Implementation', this.$store, data)
    },

    trackCompleteRev(data) {
      this.$track('completeRev', this.$store, data)
    },

    trackRevStatusChanged(rev, isEditMode, element, newStatus, reason) {
      this.$track(
        'rev.statusChanged',
        this.$store,
        {
          isEditMode,
          element,
          oldStatus: rev?.status ?? '',
          newStatus,
          ...reason,
        },
        TRACK_INTERCOM_EVENT
      )
    },

    trackRevReason(status) {
      this.$track(
        'rev.reason',
        this.$store,
        {
          status,
        },
        TRACK_INTERCOM_EVENT
      )
    },

    trackCreate(process, type, page, rev, response = {}) {
      let eventProperties = {
        process: process,
        status: response.status || null,
        error: response.error || null,
        revType: type || null,
        page: page,
      }

      if (rev) {
        eventProperties = Object.assign(eventProperties, {
          coachId: _.get(rev, 'coach.id') || null,
          revSiteId: _.get(rev, 'site') || null,
        })
      }

      this.$track(
        'rev.Create',
        this.$store,
        eventProperties,
        TRACK_INTERCOM_EVENT
      )
    },

    trackUpdate(rev, changedProperty, value, from) {
      const eventProperties = {
        from,
        change: this.updateChanges[changedProperty],
        cycle: this.getRevCycle(rev.type, rev),
        isNewCycleStep: false,
        durationInDays: this.getDurationInDays(rev),
        durationInHours: this.getDurationInHours(rev),
        verified: rev?.verify?.didExperimentWork ?? null,
        implemented: rev?.status === 'implemented',
        benefits: rev.categories.length ? rev.categories : null,
        costReduction: rev.review?.costReduction ?? null,
        investmentMade: rev.review?.investment ?? null,
        revenueGenerated: rev.review?.revenue ?? null,
        revType: rev?.type ?? '',
        revId: rev?._id ?? rev?.id,
      }

      if (rev.validation) {
        const highlighted = rev.validation?.highlight ?? false
        const implementation = rev.validation?.implementation ?? false
        const financial = rev.validation?.financial ?? false

        eventProperties.isHighlighted = highlighted
        eventProperties.isValidatedForImplementation = implementation
        eventProperties.isFinanciallyValidated = financial
      }

      if (this.initialPhase !== rev.phase) {
        this.initialPhase = rev.phase
        eventProperties.isNewCycleStep = true
      }

      if (changedProperty === 'implementation') {
        eventProperties.isValidatedForImplementation = value
      }

      if (changedProperty === 'highlight') {
        eventProperties.isValidatedForImplementation = value
        eventProperties.isHighlighted = value
      }

      if (changedProperty === 'financial') {
        eventProperties.isFinanciallyValidated = value
      }

      if (changedProperty === 'coach') {
        eventProperties.coach = value
      }

      this.$track(
        'rev.Update',
        this.$store,
        eventProperties,
        TRACK_INTERCOM_EVENT
      )
    },

    trackLike(rev, type, isLiked, status, error) {
      const eventProperties = {
        cycle: this.getRevCycle(type, rev),
        revId: rev._id || rev.id,
        revType: type,
        isLiked: isLiked,
        status: status,
        error: error || null,
      }

      this.$track(
        'rev.Like',
        this.$store,
        eventProperties,
        TRACK_INTERCOM_EVENT
      )
    },

    trackApprovals(rev, type, process, action) {
      const eventProperties = {
        cycle: this.getRevCycle(type, rev),
        revId: rev._id || rev.id,
        revType: type,
        process,
        action,
      }

      this.$track(
        'rev.approvals',
        this.$store,
        eventProperties,
        TRACK_INTERCOM_EVENT
      )
    },

    trackPrint(rev, view) {
      this.$track(
        'rev.Print',
        this.$store,
        {
          cycle: this.getRevCycle(null, rev),
          revId: rev?._id ?? rev?.id,
          view: view || null,
        },
        TRACK_INTERCOM_EVENT
      )
    },

    trackDelete(rev, type, process, status, error) {
      const eventProperties = {
        cycle: this.getRevCycle(type, rev),
        durationInDays: this.getDurationInDays(rev),
        durationInHours: this.getDurationInHours(rev),
        process: process,
        status: status || null,
        error: error || null,
        revType: type,
        revId: rev?._id ?? rev?.id,
      }

      this.$track(
        'rev.Delete',
        this.$store,
        eventProperties,
        TRACK_INTERCOM_EVENT
      )
    },

    trackAddGroup(view, levels) {
      this.$track(
        'group.Add',
        this.$store,
        { view, levels },
        TRACK_INTERCOM_EVENT
      )
    },

    trackCreateGroup(process) {
      this.$track(
        'group.Create',
        this.$store,
        { process },
        TRACK_INTERCOM_EVENT
      )
    },

    trackTool(tool, step, revType) {
      this.$track(
        'tool.Use',
        this.$store,
        { name: tool, step, revType },
        TRACK_INTERCOM_EVENT
      )
    },

    trackUserProfileView(userId, view) {
      this.$track(
        'user.Visit',
        this.$store,
        { userId, view },
        TRACK_INTERCOM_EVENT
      )
    },

    trackAlert(action, type, view) {
      this.$track(`alert.${action}`, this.$store, { type, view })
    },

    trackTranslationButton() {
      this.$track('rev.Translate', this.$store, {
        clickedElement: 'headerButton',
      })
    },

    trackTranslationComment() {
      this.$track('rev.Translate', this.$store, { clickedElement: 'comment' })
    },

    trackTranslationAlert() {
      this.$track('rev.Translate', this.$store, { clickedElement: 'alert' })
    },

    trackRoutineCreation(data) {
      this.$track('routineDefinition.Create', this.$store, data)
    },

    trackRoutineUpdate(data) {
      this.$track('routineDefinition.Edit', this.$store, data)
    },

    trackRoutineDelete(data = {}) {
      this.$track('routineDefinition.Delete', this.$store, data)
    },

    trackStartRoutine(data) {
      this.$track('routineExecution.Start', this.$store, data)
    },

    trackPrintRoutine(data) {
      this.$track('routineExecution.Print', this.$store, data)
    },

    trackRoutineExecutionDelete(data = {}) {
      this.$track('routineExecution.Delete', this.$store, data)
    },

    trackRoutineFormNavigate(data) {
      this.$track('routineDefinition.Navigate', this.$store, data)
    },

    trackCompleteRoutine(data) {
      this.$track('routineExecution.Complete', this.$store, data)
    },

    trackSavedRoutine(data) {
      this.$track('routineExecution.Saved', this.$store, data)
    },

    trackDownloadQr(data) {
      this.$track('routineQr.download', this.$store, data)
    },

    trackRoutineQrView(data = {}) {
      this.$track('routineQr.view', this.$store, data)
    },

    trackCreateFinding(data) {
      this.$track('finding.Create', this.$store, data)
    },

    trackEditFinding(data) {
      this.$track('finding.Edit', this.$store, data)
    },

    trackDeleteFinding(data = {}) {
      this.$track('finding.Delete', this.$store, data)
    },

    trackAddressFinding(data) {
      this.$track('finding.Address', this.$store, data)
    },

    trackFindingStatusChanged(finding, element, newStatus, reason) {
      this.$track(
        'finding.statusChanged',
        this.$store,
        {
          element,
          oldStatus: finding?.status ?? '',
          newStatus,
          ...reason,
        },
        TRACK_INTERCOM_EVENT
      )
    },

    trackFindingReason(status) {
      this.$track(
        'finding.reason',
        this.$store,
        {
          status,
        },
        TRACK_INTERCOM_EVENT
      )
    },

    trackHomeVisit(data) {
      this.$track('home.Visit', this.$store, data)
    },

    trackHomeNavigate(data) {
      this.$track('home.Navigate', this.$store, data)
    },

    trackHomeCustomize(data) {
      this.$track('home.Customize', this.$store, data)
    },

    trackTeamsSearch(data) {
      this.$track('teams.Search', this.$store, data)
    },

    trackTeamDelete(data) {
      this.$track('teams.Delete', this.$store, data)
    },

    trackTeamEdit(data) {
      this.$track('teams.Edit', this.$store, data)
    },

    trackTeamNavigate(data) {
      this.$track('teams.Navigate', this.$store, data)
    },

    trackMenuNavigate(data) {
      this.$track('menu.Navigate', this.$store, data)
    },

    trackDirectoryFilter(data) {
      this.$track('directory.Filter', this.$store, data)
    },

    trackDirectorySearch(data) {
      this.$track('directory.Search', this.$store, data)
    },

    trackDirectoryNavigate(data) {
      this.$track('directory.Navigate', this.$store, data)
    },

    trackProfileNavigate(data) {
      this.$track('profile.Navigate', this.$store, data)
    },

    trackPeopleSearch(data) {
      this.$track('admin_people.Search', this.$store, data)
    },

    trackPeopleNavigate(data) {
      this.$track('admin_people.Navigate', this.$store, data)
    },

    trackPeopleFilter(data) {
      this.$track('admin_people.Filter', this.$store, data)
    },

    trackAddPeopleNavigate(data) {
      this.$track('add_people.Navigate', this.$store, data)
    },

    trackAddPeople(data) {
      this.$track('add_people.AddUsers', this.$store, data)
    },

    trackRecognitionsNavigate(data) {
      this.$track('recognitions.Navigate', this.$store, data)
    },
    trackExploreFilter(data) {
      this.$track('explore.Filter', this.$store, data)
    },
    trackExploreColumnsSelector(data) {
      this.$track('explore.ColumnsSelector', this.$store, data)
    },
    trackExplorePaginate(data) {
      this.$track('explore.Paginate', this.$store, data)
    },
    trackExploreView(data) {
      this.$track('explore.View', this.$store, data)
    },
    trackExploreSort(data) {
      this.$track('explore.Sort', this.$store, data)
    },
    trackExploreSearch(data) {
      this.$track('explore.Search', this.$store, data)
    },
    trackExploreNavigate(data) {
      this.$track('explore.Navigate', this.$store, data)
    },
    trackExploreHistory(data) {
      this.$track('explore.History', this.$store, data)
    },
    trackChecklistAdd(data) {
      this.$track('checklist.Add', this.$store, data)
    },
    trackChecklistDelete(data) {
      this.$track('checklist.Delete', this.$store, data)
    },
    trackChecklistEdit() {
      this.$track('checklist.Edit', this.$store, {})
    },
    trackChecklistCreate() {
      this.$track('checklist.Create', this.$store, {})
    },
    trackChecklistNavigate(data) {
      this.$track('checklist.Navigate', this.$store, data)
    },
    trackChecklistEnable(data) {
      this.$track('checklist.Enable', this.$store, data)
    },
    trackChecklistPrivate(data) {
      this.$track('checklist.Private', this.$store, data)
    },
    trackChecklistImport(data) {
      this.$track('checklist.Import', this.$store, data)
    },
    trackChecklistResponsesDownloadReport(data) {
      this.$track('checklistResponses.DownloadReport', this.$store, data)
    },
    trackChecklistResponsesFilterResults(data) {
      this.$track('checklistResponses.FilterEntity', this.$store, data)
    },
    trackChecklistResponsesDrawerOpened(data) {
      this.$track('checklistResponses.DrawerOpened', this.$store, data)
    },
    trackChecklistResponsesEntityVisited(data) {
      this.$track('checklistResponses.EntityVisited', this.$store, data)
    },
    trackChecklistResponsesClickInsightCard(data) {
      this.$track('checklistResponses.ClickInsightCard', this.$store, data)
    },
    trackOpenContextualMenu(data) {
      this.$track('rev.ContextualMenu', this.$store, data)
    },
    trackClickValidateIcon(data) {
      this.$track('rev.ValidateIcon', this.$store, data)
    },
    trackClickReviewValidationFilter(data) {
      this.$track('rev.ReviewFilter', this.$store, data)
    },
    trackValidationRequest(data) {
      this.$track('validation.Request', this.$store, data)
    },
    trackValidationPartialSave(data) {
      this.$track('validation.PartialSave', this.$store, data)
    },
    trackValidationValidate(data) {
      this.$track('validation.Validate', this.$store, data)
    },
    trackDeleteValidation(data) {
      this.$track('validation.Delete', this.$store, data)
    },
    trackValidationsView(data) {
      this.$track('validations.View', this.$store, data)
    },
    trackValidationsNavigation(data) {
      this.$track('validation.Navigate', this.$store, data)
    },
    trackValidationsActions(data) {
      this.$track('validations.Actions', this.$store, data)
    },
    trackValidationsColumns(data) {
      this.$track('validations.Columns', this.$store, data)
    },
    trackChangeDate(data) {
      this.$track('validation.ChangeDate', this.$store, data)
    },
    trackBenefitsEstimation(data) {
      this.$track('validation.BenefitsEstimation', this.$store, data)
    },
    trackAddImpactMetric(data) {
      this.$track('validation.AddImpactMetric', this.$store, data)
    },
    trackDeleteImpactMetric(data) {
      this.$track('validation.DeleteImpactMetric', this.$store, data)
    },
    trackAddInvestment(data) {
      this.$track('validation.AddInvestment', this.$store, data)
    },
    trackDeleteInvestment(data) {
      this.$track('validation.DeleteInvestment', this.$store, data)
    },
    trackGenerateReport(data) {
      this.$track('reports.generateReport', this.$store, data)
    },
    trackDownloadReport(data) {
      this.$track('reports.downloadReport', this.$store, data)
    },
    trackRemoveReport(data) {
      this.$track('reports.removeReport', this.$store, data)
    },
    trackClearReportList(data) {
      this.$track('reports.clearReportList', this.$store, data)
    },
    trackOpenReportList(data) {
      this.$track('reports.openReportList', this.$store, data)
    },

    trackFilterActions(eventName, data = {}) {
      this.$track(eventName, this.$store, data)
    },

    trackBoardsActions(eventName, data = {}) {
      this.$track(eventName, this.$store, data)
    },

    trackTasks(eventName, data = {}) {
      this.$track(eventName, this.$store, data)
    },

    trackRoutineDefinitionReminder(eventName, data = {}) {
      this.$track(eventName, this.$store, data)
    },

    trackRoutineList(data = {}) {
      this.$track('routine.ListActions', this.$store, data)
    },

    trackRoutineDetailsTable(data = {}) {
      this.$track('routine.DetailsTableActions', this.$store, data)
    },

    trackRoutineViewNavigate(data) {
      this.$track('routineDefinition.Navigate', this.$store, data)
    },

    trackViewAllItems(data = {}) {
      this.$track('viewAllItems', this.$store, data)
    },

    trackInsightsFeedbackLike(data = {}) {
      this.$track('insightsFeedback.Like', this.$store, data)
    },

    trackInsightsFeedbackDisLike(data = {}) {
      this.$track('insightsFeedback.Dislike', this.$store, data)
    },

    trackInsightsWidgetNavigate(data) {
      this.$track('insightsWidget.Navigate', this.$store, data)
    },

    trackAIResponseLike(data) {
      this.$track('aiChat.Like', this.$store, data)
    },

    trackAIResponseDislike(data) {
      this.$track('aiChat.Dislike', this.$store, data)
    },
  },
}
</script>
